// Shared file library — ONE stored copy per file for the whole tree.
//
// Files are uploaded at a level (MLS / org / agent) and referenced by reminders by
// id; they are never embedded into or copied between reminders. Public files are
// inherited (read-only) by lower levels.
//
// STORAGE MODEL (server-authoritative): file BYTES live only on the server
// (/api/files — backed by the ira_files/_global ScopedDoc). The browser's
// localStorage `ira_files` map holds METADATA ONLY (hydrated on boot via
// /api/store/mine, which strips bytes) — so uploads no longer eat the ~5MB
// localStorage quota, and files follow the account to any browser. Bytes are
// fetched on demand: viewing opens /api/files/:id/data; attachments fetch and
// inline the data at send time (resolveAttachments). In local-only mode (no API)
// the old everything-in-localStorage behavior remains.
//
// owner is a string key: `mls:<mlsId>` | `org:<orgId>` | `agent:<accountId>`.
// record: { id, name, title, ext, type, size, public, relays, owner, createdAt[, data] }
import { api, usingApi } from './api';

const FILES_KEY = 'ira_files';
export const MAX_FILE_BYTES = 1024 * 1024;          // 1 MB/file (stored once, so a bit larger)
export const FILE_ACCEPT = '.pdf,.txt,.png,.jpg,.jpeg,.webp,.gif,application/pdf,text/plain,image/*';

const IMG_RE = /\.(png|jpe?g|webp|gif)$/i;

export function fmtSize(b) {
  return b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`;
}
export function isSupportedFile(file) {
  return file && (
    file.type === 'application/pdf' || file.type === 'text/plain' || /^image\//.test(file.type || '')
    || /\.(pdf|txt)$/i.test(file.name) || IMG_RE.test(file.name)
  );
}
// Is a STORED record an image? (Drives the Graphs & Images section split and the
// public profile's Charts & Statistics rendering.)
export function isImageFile(f) {
  return !!f && (/^image\//.test(f.type || '') || IMG_RE.test(f.ext || f.name || ''));
}
export function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error('Could not read file'));
    r.readAsDataURL(file);
  });
}

function loadImage(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not read the image'));
    img.src = dataUrl;
  });
}

// Read an upload, auto-compressing images: everything is stored as a base64 data-URL
// inside the shared JSON doc (localStorage-quota territory), so a multi-MB phone photo
// would crowd out the whole app. Images are downscaled to ≤1400px and re-encoded as
// JPEG q0.85 on a white background (JPEG has no alpha). Small images and GIFs (would
// lose animation) pass through untouched; if re-encoding doesn't actually shrink the
// file, the original wins. Returns { data, size } — size is the STORED byte count.
export async function prepareUpload(file) {
  const raw = await readFileAsDataUrl(file);
  const isImg = /^image\//.test(file.type || '') || IMG_RE.test(file.name || '');
  const isGif = file.type === 'image/gif' || /\.gif$/i.test(file.name || '');
  if (!isImg || isGif) return { data: raw, size: file.size };
  const img = await loadImage(raw);
  const MAX_DIM = 1400;
  const scale = Math.min(1, MAX_DIM / Math.max(img.width || 1, img.height || 1));
  if (scale === 1 && file.size <= 400 * 1024) return { data: raw, size: file.size };
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(img.width * scale));
  canvas.height = Math.max(1, Math.round(img.height * scale));
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  const out = canvas.toDataURL('image/jpeg', 0.85);
  const bytes = Math.round((out.length - out.indexOf(',') - 1) * 3 / 4);
  return bytes < file.size ? { data: out, size: bytes } : { data: raw, size: file.size };
}

export function loadFiles() {
  try { return JSON.parse(localStorage.getItem(FILES_KEY) || '{}') || {}; } catch { return {}; }
}
function saveFiles(map) {
  localStorage.setItem(FILES_KEY, JSON.stringify(map));   // may throw QuotaExceededError — callers handle
}

// Add a file. Returns the new record. Throws on quota overflow (caller shows the error).
// Original file extension (e.g. ".pdf"), captured at upload so emailed attachments
// keep it even after the display name is renamed without it.
export function fileExt(name) {
  return (String(name || '').match(/\.[a-z0-9]+$/i) || [''])[0].toLowerCase();
}

// Add a file. API mode: the bytes go to the server; only metadata lands in the
// local map. Local mode: the old everything-local behavior. ASYNC — await it.
export async function addFile({ owner, name, type, size, data, isPublic }) {
  if (usingApi()) {
    const { file } = await api.post('/api/files/upload', { owner, name, type, data, isPublic: !!isPublic });
    const map = loadFiles();
    map[file.id] = file;   // metadata only — no bytes in localStorage
    saveFiles(map);
    return file;
  }
  const map = loadFiles();
  const id = `file__${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
  // `public` is the OWNER's flag: does it pass to the level directly below me?
  // `relays` is { <levelOwnerKey>: true } — does an inheriting level pass it on
  // FURTHER down? Both default to off, so a new file is Private until shared.
  // `name` = the original filename (stable — used for attachments). `title` = the
  // agent-set descriptive label used as the hyperlink text on the public profile;
  // it defaults to the filename minus extension so it's never blank.
  const title = String(name || '').replace(/\.[a-z0-9]+$/i, '') || String(name || '');
  map[id] = { id, name, title, ext: fileExt(name), type: type || 'application/octet-stream', size: size || 0, data, public: !!isPublic, relays: {}, owner, createdAt: new Date().toISOString() };
  saveFiles(map);
  return map[id];
}

// Refresh the local metadata map from the server (call on Files-screen mount so a
// fresh browser sees the whole library without a full reload).
export async function hydrateFiles() {
  if (!usingApi()) return;
  try {
    const { files } = await api.get('/api/files/lib');
    if (files && typeof files === 'object') saveFiles(files);
  } catch { /* offline — keep the local copy */ }
}

// Set whether an inheriting level (mls / org) RELAYS an inherited file on to its
// own children. levelKey is that level's owner key (e.g. 'org:<id>'). Stored on
// the file so the (one) shared record carries every level's relay decision.
// Optimistic: local map updates now, the server PATCH follows.
export function setFileRelay(fileId, levelKey, isPublic) {
  const map = loadFiles();
  const f = map[fileId];
  if (!f) return;
  const relays = { ...(f.relays || {}) };
  if (isPublic) relays[levelKey] = true; else delete relays[levelKey];
  map[fileId] = { ...f, relays };
  saveFiles(map);
  if (usingApi()) api.patch(`/api/files/${encodeURIComponent(fileId)}`, { relay: { key: levelKey, on: !!isPublic } }).catch(() => {});
}

// Filename for an emailed attachment: the friendly (display) name, but with the
// original extension restored if it was renamed away — so recipients see ".pdf" etc.
export function attachmentName(f) {
  if (!f) return '';
  const ext = f.ext || fileExt(f.name);
  if (!ext) return f.name;
  return String(f.name).toLowerCase().endsWith(ext) ? f.name : f.name + ext;
}
// Title saves are DEBOUNCED: the rename input fires on every keystroke, and a
// per-keystroke PATCH both spams the server and (before the server serialized its
// writes) could race so a stale prefix landed last — "rename doesn't stick".
// One PATCH goes out shortly after typing stops; flushTitleSave() (wired to the
// input's blur) sends immediately so a quick save-then-refresh can't lose it.
const titleTimers = new Map();   // fileId -> timeout
const TITLE_SAVE_MS = 500;
function sendTitlePatch(id) {
  titleTimers.delete(id);
  const f = loadFiles()[id];
  if (!f || !usingApi()) return;
  api.patch(`/api/files/${encodeURIComponent(id)}`, { title: f.title != null ? String(f.title) : '' }).catch(() => {});
}
export function flushTitleSave(id) {
  const t = titleTimers.get(id);
  if (t) { clearTimeout(t); sendTitlePatch(id); }
}

// Optimistic: local metadata updates now, the server call follows. Only `title`
// and `public` are server-persisted fields (the rest is derived at upload).
export function updateFile(id, patch) {
  const map = loadFiles();
  if (!map[id]) return;
  map[id] = { ...map[id], ...patch };
  saveFiles(map);
  if (usingApi()) {
    if (typeof patch.title === 'string') {
      clearTimeout(titleTimers.get(id));
      titleTimers.set(id, setTimeout(() => sendTitlePatch(id), TITLE_SAVE_MS));
    }
    if (typeof patch.public === 'boolean') {
      api.patch(`/api/files/${encodeURIComponent(id)}`, { public: patch.public }).catch(() => {});
    }
  }
}
export function removeFile(id) {
  const map = loadFiles();
  if (map[id]) { delete map[id]; saveFiles(map); }
  if (usingApi()) api.delete(`/api/files/${encodeURIComponent(id)}`).catch(() => {});
}

// URL that streams a stored file's bytes from the server (auth cookie rides along).
export function fileDataUrl(id) {
  return `/api/files/${encodeURIComponent(id)}/data`;
}

// Files a given owner uploaded (all of them, public or private).
export function listOwned(ownerKey) {
  return Object.values(loadFiles())
    .filter(f => f.owner === ownerKey)
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

// A level can ARCHIVE (hide) inherited files it doesn't want cluttering its lists/pickers.
// This is per-owner and local — it doesn't affect the file or other levels.
function hiddenKey(ownerKey) { return 'ira_files_hidden__' + ownerKey; }
export function loadHidden(ownerKey) {
  if (!ownerKey) return [];
  try { return JSON.parse(localStorage.getItem(hiddenKey(ownerKey)) || '[]') || []; } catch { return []; }
}
export function archiveFile(ownerKey, fileId) {
  const set = new Set(loadHidden(ownerKey)); set.add(fileId);
  localStorage.setItem(hiddenKey(ownerKey), JSON.stringify([...set]));
}
export function unarchiveFile(ownerKey, fileId) {
  const set = new Set(loadHidden(ownerKey)); set.delete(fileId);
  localStorage.setItem(hiddenKey(ownerKey), JSON.stringify([...set]));
}

// Files visible for building a reminder/message at some level:
//   ownKeys      → owners whose files you see in full (your own level)
//   ancestors    → ORDERED parent owner keys, nearest parent first (e.g. an agent
//                  passes ['org:<id>', 'mls:<id>']). A file owned by an ancestor is
//                  visible only if the owner published it (f.public) AND every level
//                  BETWEEN that owner and you relayed it on (f.relays[level]). This
//                  is the reminder-style cascade: each hop re-decides, default off.
//   publicKeys   → flat back-compat: any PUBLIC file owned by one of these keys
//                  (used by the System picker, which sits above all MLSs).
//   hideOwnerKey → whose archive list to honor (defaults to your own level, ownKeys[0]);
//                  archived INHERITED files are excluded. Own files are never hidden.
// Returns [{ ...record, inherited: bool }].
export function visibleFiles({ ownKeys = [], ancestors = [], publicKeys = [], hideOwnerKey } = {}) {
  const own = new Set(ownKeys);
  const flatPub = new Set(publicKeys);
  const hk = hideOwnerKey !== undefined ? hideOwnerKey : ownKeys[0];
  const hidden = new Set(hk ? loadHidden(hk) : []);
  const out = [];
  for (const f of Object.values(loadFiles())) {
    if (own.has(f.owner)) { out.push({ ...f, inherited: false }); continue; }
    let visible = false;
    const ai = ancestors.indexOf(f.owner);
    if (ai !== -1 && f.public) {
      visible = true;
      const relays = f.relays || {};
      for (let j = 0; j < ai; j++) {            // levels nearer to me than the owner
        if (relays[ancestors[j]] !== true) { visible = false; break; }
      }
    }
    if (!visible && flatPub.has(f.owner) && f.public) visible = true;   // System-picker fallback
    if (visible && !hidden.has(f.id)) out.push({ ...f, inherited: true });
  }
  return out.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

// Resolve reminder file-id references to records (skip any that were deleted).
export function resolveFiles(ids) {
  const map = loadFiles();
  return (ids || []).map(id => map[id]).filter(Boolean);
}

// Resolve file ids into EMAIL ATTACHMENTS [{ filename, path: dataURL }], fetching
// bytes from the server for metadata-only records (the normal case now). Legacy
// records that still carry data locally use it directly. ASYNC — await at send time.
export async function resolveAttachments(ids) {
  const out = [];
  for (const f of resolveFiles(ids)) {
    if (f.data) { out.push({ filename: attachmentName(f), path: f.data }); continue; }
    try {
      const r = await fetch(fileDataUrl(f.id), { credentials: 'include' });
      if (!r.ok) continue;   // deleted server-side — skip rather than fail the send
      const blob = await r.blob();
      const dataUrl = await new Promise((resolve, reject) => {
        const rd = new FileReader();
        rd.onload = () => resolve(rd.result);
        rd.onerror = () => reject(new Error('read failed'));
        rd.readAsDataURL(blob);
      });
      out.push({ filename: attachmentName(f), path: dataUrl });
    } catch { /* unreachable file — send without it */ }
  }
  return out;
}

// ── Viewing file content ──────────────────────────────────────────────────────
// A stored file's bytes live in a data: URL. Modern browsers block top-level
// navigation straight to a data: URL (especially for PDFs), so to let anyone —
// the owner or a lower level that inherits the public file — actually review the
// content before attaching it, we turn the data: URL into a Blob and open that.
export function fileBlob(f) {
  const dataUrl = String((f && f.data) || '');
  const comma = dataUrl.indexOf(',');
  const meta = comma >= 0 ? dataUrl.slice(0, comma) : '';
  const b64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
  const mime = (meta.match(/data:([^;]+)/) || [])[1] || f.type || 'application/octet-stream';
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

// Open a stored file's content in a new browser tab (PDF viewer / image / text).
// Metadata-only records (the normal case) open the server's streaming URL directly —
// the browser renders it natively and the session cookie authorizes it. Legacy
// records with local bytes use the blob path. Call from a click handler so the
// pop-up isn't blocked.
export function openFile(f) {
  if (!f) return false;
  if (!f.data) {
    if (!f.id) return false;
    try { return !!window.open(fileDataUrl(f.id), '_blank', 'noopener'); } catch { return false; }
  }
  try {
    const url = URL.createObjectURL(fileBlob(f));
    const win = window.open(url, '_blank', 'noopener');
    // Revoke after the tab has had time to load; also covers a blocked pop-up.
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return !!win;
  } catch {
    try { return !!window.open(f.data, '_blank', 'noopener'); } catch { return false; }
  }
}
